import type {
  DagPathState,
  DagRecordType,
  DagViewerGraph,
  DagViewerNode,
} from "./types";
import type { CanonicalModelInstanceClassification } from "../../api/canonicalLineage";

export const ALL_DAG_RECORD_TYPES: DagRecordType[] = [
  "artifact",
  "version",
  "operation",
  "human",
  "decision",
  "lifecycle",
];

export const ALL_DAG_PATH_STATES: DagPathState[] = ["active", "human", "invalidated", "context"];
export const ALL_MODEL_CLASSIFICATIONS: CanonicalModelInstanceClassification[] = [
  "generic",
  "instance",
  "unclassified",
];

export type DagTopologyFacet = "root" | "leaf" | "anchor";
export type DagTimeWindowDays = 7 | 30 | 90 | null;

export interface DagFilterState {
  recordTypes: DagRecordType[];
  pathStates: DagPathState[];
  modelClassifications: CanonicalModelInstanceClassification[];
  topology: DagTopologyFacet[];
  timeWindowDays: DagTimeWindowDays;
}

export const DEFAULT_DAG_FILTERS: DagFilterState = {
  recordTypes: [...ALL_DAG_RECORD_TYPES],
  pathStates: [...ALL_DAG_PATH_STATES],
  modelClassifications: [...ALL_MODEL_CLASSIFICATIONS],
  topology: [],
  timeWindowDays: null,
};

export interface DagFilterResult {
  mask: Uint8Array | null;
  visibleCount: number;
  activeGroupCount: number;
}

export type DagSearchField = "any" | "label" | "kind" | "type" | "state" | "channel" | "path" | "id" | "digest" | "anchor" | "after" | "before";

export interface DagSearchToken {
  field: DagSearchField;
  /** Original field spelling, including unsupported fields, for strict live compilation. */
  sourceField: string | null;
  value: string;
  negated: boolean;
}

export interface DagSearchDocument {
  index: number;
  timestampMs: number | null;
  fields: Record<Exclude<DagSearchField, "after" | "before">, string>;
}

const SEARCH_FIELD_ALIASES: Record<string, DagSearchField | undefined> = {
  label: "label",
  name: "label",
  kind: "kind",
  type: "type",
  state: "state",
  status: "state",
  channel: "channel",
  path: "path",
  id: "id",
  digest: "digest",
  hash: "digest",
  anchor: "anchor",
  after: "after",
  before: "before",
};

const CURATED_METADATA_KEYS = [
  "title",
  "label",
  "name",
  "story",
  "tool",
  "operation_kind",
  "artifact_name",
  "version_label",
] as const;

function normalized(value: unknown): string {
  if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
    return String(value).trim().toLocaleLowerCase();
  }
  return "";
}

function rawScalar(node: DagViewerNode, key: string): string {
  return normalized((node.raw as Record<string, unknown>)[key]);
}

function metadataScalars(node: DagViewerNode): string {
  return CURATED_METADATA_KEYS.map((key) => normalized(node.metadata[key])).filter(Boolean).join("\u0000");
}

export function buildDagSearchDocuments(graph: DagViewerGraph): DagSearchDocument[] {
  return graph.nodes.map((node, index) => {
    const label = [node.label, rawScalar(node, "name")].map(normalized).filter(Boolean).join("\u0000");
    const kind = normalized(node.kind);
    const type = normalized(node.type);
    const state = [node.state, node.pathState].map(normalized).filter(Boolean).join("\u0000");
    const channel = normalized(node.channel);
    const path = rawScalar(node, "path");
    const id = normalized(node.id);
    const digest = normalized(node.digest);
    const anchor = node.anchor ? "true yes anchor" : "false no";
    const metadata = metadataScalars(node);
    return {
      index,
      timestampMs: node.timestamp && Number.isFinite(Date.parse(node.timestamp)) ? Date.parse(node.timestamp) : null,
      fields: {
        label,
        kind,
        type,
        state,
        channel,
        path,
        id,
        digest,
        anchor,
        any: [label, kind, type, state, channel, path, id, digest, metadata].filter(Boolean).join("\u0000"),
      },
    };
  });
}

export function parseDagSearch(query: string): DagSearchToken[] {
  const tokens: DagSearchToken[] = [];
  const expression = /(-?)(?:([a-zA-Z]+):)?(?:"([^"]+)"|(\S+))/g;
  for (const match of query.matchAll(expression)) {
    const rawField = match[2]?.toLocaleLowerCase();
    const knownField = rawField ? SEARCH_FIELD_ALIASES[rawField] : undefined;
    const rawValue = (match[3] ?? match[4] ?? "").trim();
    if (!rawValue) continue;
    tokens.push({
      field: knownField ?? "any",
      sourceField: rawField ?? null,
      value: normalized(rawField && !knownField ? `${rawField}:${rawValue}` : rawValue),
      negated: match[1] === "-",
    });
  }
  return tokens;
}

function matchesToken(document: DagSearchDocument, token: DagSearchToken): boolean {
  if (token.field === "after" || token.field === "before") {
    const boundary = Date.parse(token.value);
    if (!Number.isFinite(boundary) || document.timestampMs == null) return false;
    return token.field === "after" ? document.timestampMs >= boundary : document.timestampMs <= boundary;
  }
  return document.fields[token.field].includes(token.value);
}

function matchRank(document: DagSearchDocument, tokens: DagSearchToken[]): number {
  let score = 0;
  for (const token of tokens) {
    if (token.negated || token.field === "after" || token.field === "before") continue;
    const label = document.fields.label;
    if (label === token.value) score -= 100;
    else if (label.startsWith(`${token.value} `) || label.startsWith(`${token.value}\u0000`)) score -= 72;
    else if (label.startsWith(token.value)) score -= 60;
    else if (label.includes(token.value)) score -= 30;
    else if (document.fields.kind.startsWith(token.value)) score -= 18;
    else if (document.fields.path.includes(token.value)) score -= 10;
  }
  return score;
}

export function findDagMatches(
  graph: DagViewerGraph,
  documents: DagSearchDocument[],
  query: string,
  filterMask: Uint8Array | null = null,
): number[] {
  const tokens = parseDagSearch(query);
  if (!tokens.length) return [];
  return documents
    .filter((document) => {
      if (filterMask && !filterMask[document.index]) return false;
      return tokens.every((token) => matchesToken(document, token) !== token.negated);
    })
    .sort((a, b) => {
      const rank = matchRank(a, tokens) - matchRank(b, tokens);
      if (rank) return rank;
      const time = (b.timestampMs ?? -Infinity) - (a.timestampMs ?? -Infinity);
      if (time) return time;
      return graph.nodes[a.index]!.label.localeCompare(graph.nodes[b.index]!.label)
        || graph.nodes[a.index]!.id.localeCompare(graph.nodes[b.index]!.id);
    })
    .map((document) => document.index);
}

export function indicesToMask(nodeCount: number, indices: number[]): Uint8Array {
  const mask = new Uint8Array(nodeCount);
  for (const index of indices) {
    if (index >= 0 && index < nodeCount) mask[index] = 1;
  }
  return mask;
}

function hasEvery<T>(values: T[], expected: T[]): boolean {
  const valueSet = new Set(values);
  return expected.every((value) => valueSet.has(value));
}

export function buildDagFilterMask(graph: DagViewerGraph, filters: DagFilterState): DagFilterResult {
  const typesAreDefault = filters.recordTypes.length === ALL_DAG_RECORD_TYPES.length
    && hasEvery(filters.recordTypes, ALL_DAG_RECORD_TYPES);
  const statesAreDefault = filters.pathStates.length === ALL_DAG_PATH_STATES.length
    && hasEvery(filters.pathStates, ALL_DAG_PATH_STATES);
  const activeGroupCount = Number(!typesAreDefault)
    + Number(!statesAreDefault)
    + Number(filters.topology.length > 0)
    + Number(filters.timeWindowDays != null);
  if (activeGroupCount === 0) return { mask: null, visibleCount: graph.nodes.length, activeGroupCount };

  const types = new Set(filters.recordTypes);
  const states = new Set(filters.pathStates);
  const topology = new Set(filters.topology);
  const latestTimestamp = filters.timeWindowDays == null
    ? null
    : graph.nodes.reduce<number | null>((latest, node) => {
        const parsed = node.timestamp ? Date.parse(node.timestamp) : Number.NaN;
        return Number.isFinite(parsed) ? Math.max(latest ?? -Infinity, parsed) : latest;
      }, null);
  const earliestTimestamp = latestTimestamp == null || filters.timeWindowDays == null
    ? null
    : latestTimestamp - filters.timeWindowDays * 86_400_000;
  const mask = new Uint8Array(graph.nodes.length);
  let visibleCount = 0;

  graph.nodes.forEach((node, index) => {
    if (!types.has(node.type) || !states.has(node.pathState)) return;
    if (topology.size > 0) {
      const matchesTopology = (topology.has("root") && graph.incoming[index]!.length === 0)
        || (topology.has("leaf") && graph.outgoing[index]!.length === 0)
        || (topology.has("anchor") && node.anchor);
      if (!matchesTopology) return;
    }
    if (earliestTimestamp != null) {
      const timestamp = node.timestamp ? Date.parse(node.timestamp) : Number.NaN;
      if (!Number.isFinite(timestamp) || timestamp < earliestTimestamp) return;
    }
    mask[index] = 1;
    visibleCount += 1;
  });

  return { mask, visibleCount, activeGroupCount };
}
